import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor, vdjBeatChroma, vdjCameraKick, VdjPost, VdjComfort, VDJ_PRESETS, VDJ_DROP_FLASH } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Un quad plein écran, caméra fixe.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'isocubes',
  family: 'geometric',
  danceability: 0.88,
  energyLevel: 0.80,
  bpmMin: 110,
  bpmMax: 140,
  movement: 0.80,
  tonality: 0.55,
  prefers: { gridStrength: [0.6, 1.0] },
  enabled: true
};

// Isocubes : un champ infini de colonnes vues en isométrie, le motif op-art
// des « blocs culbutants » (trois faces par cube : claire, moyenne, sombre),
// qui monte et descend en vagues sur la musique ; parfois le motif bascule
// et les cubes deviennent des creux (illusion de Necker). House, techno,
// électro, nu-disco. Règles R1-R20 de kiosk-app/CLAUDE.md et preset party
// de lib/vdjLook.js.
//
// LA GRILLE : des colonnes sur une grille CARRÉE, vues en projection
// orthographique isométrique exacte (caméra à 35,26° d'élévation et 45°
// d'azimut, direction -(1,1,1)/√3). Sous cette projection la grille carrée
// EST le réseau hexagonal des blocs culbutants : chaque cube se projette en
// hexagone régulier, les centres forment un réseau hexagonal. Trois faces
// visibles : le dessus (+z), la face +x, la face +y, trois tons fixes.
// 30 à 40 colonnes en largeur (tier 0 : 20).
//
// LE RENDU : un seul quad plein écran. Pour chaque pixel, le rayon
// orthographique part du plan z = HTOP au-dessus du champ et parcourt les
// cellules par DDA (48 pas au plus) ; dans chaque cellule, si le rayon
// descend sous la hauteur de la colonne avant d'en sortir, il touche le
// dessus ; s'il entre déjà sous la hauteur, il a touché la face par
// laquelle il est entré (+x ou +y). Premier impact = le plus proche :
// l'ordre de profondeur est exact par construction, aucune facette ne
// transperce. Les hauteurs vivent dans une texture 64 x 64 (R hauteur,
// G trace lumineuse, B masque d'accent), mise à jour chaque frame par un
// petit shader en ping-pong (demi-flottant quand le GPU le permet, sinon
// octets).
//
// LES LISERÉS (R1) : distance aux arêtes du dessus (fract des coordonnées
// de cellule) et à l'arête haute des faces (h - z), converties en pixels :
// sous cette projection, une distance d dans le plan d'une face vaut
// d / √2 / (unités monde par pixel) à l'écran, pour les arêtes des dessus
// comme pour celles des faces. Cœur blanc 0-1,2 px (au-dessus du seuil de
// bloom), gaine accent jusqu'à 3 px, halo exp(-d/8) x 0,3.
//
// LES VAGUES : h = sol + radiale (un anneau part du centre à chaque temps,
// amplitude bands.low) + plane diagonale (une période par mesure, direction
// changée à chaque mesure, amplitude bands.mid) + bruit fin par colonne
// (renouvelé toutes les croches, lissé, amplitude bands.high) + forme +
// montée. Les sommets au-dessus du seuil prennent la couleur d'accent et
// laissent une trace sur leur dessus qui s'éteint en 1 temps (tier 0 : pas
// de trace).
//
// LES FORMES : cercle, anneau, croix, spirale, ou quatre lettres du titre
// (glyphes rasterisés à 64 px, champ de distance calculé une fois par
// morceau) ; une forme se lève en 1 mesure, tient 2 mesures, retombe en 1.
// Une par scène, levée vers 6 s (scene-clock, 32 s), puis sur les phrases.
//
// L'INVERSION : sur la mesure qui suit un drop, les tons clair et sombre
// s'échangent et les hauteurs deviennent des profondeurs (h -> HINV - h) :
// le cerveau bascule et voit des cubes creux. Fondu de 1/2 temps, deux fois
// par scène au plus.
//
// LE RYTHME (party, R9) : TEMPS onde radiale et flash des liserés ; MESURE
// onde diagonale et changement de direction, à-coup de zoom (vdjCameraKick,
// zoom seul : un roulis casserait l'isométrie) ; PHRASE forme et palette à
// trois tons suivante ; MONTÉE le champ se fige puis toutes les colonnes
// montent ; DROP toutes retombent d'un coup, flash (VdjComfort), inversion.
// CONFORT (R10) : luminance mesurée toutes les 0,5 s sur un rendu 32 x 32
// du même shader, gain par VdjComfort (0,45).
//
// Debug : ?isoinvert=1 force l'inversion ; ?isoshape=circle|ring|cross|
// spiral|text force la forme (toujours levée) ; ?isoheight=1 montre le
// champ de hauteurs vu d'en haut.

const N = 64;                       // la texture des hauteurs
const FLOOR = 0.7, HSCALE = 4.5, HTOP = 4.6, HINV = FLOOR + 3.3;
const TRACE_H = 2.1;                // au-dessus : sommet d'accent, trace
const MAX_STEPS = 48;
const RADIAL_CELLS_PER_BEAT = 11;
const SCENE_S = 32, SHAPE_AT_S = 6;
const SHAPES = ['circle', 'ring', 'cross', 'spiral', 'text'];
const SHAPE_ID = { none: 0, circle: 1, ring: 2, cross: 3, spiral: 4, text: 5 };
const TEXT_W = 256, TEXT_H = 72, SDF_W = 128, SDF_H = 36, SDF_RANGE = 14;
const TEXT_CELLS = 26;              // largeur de la texture texte, en cellules
const TIER = {
  0: { cols: 20, trace: 0 },
  1: { cols: 32, trace: 1 },
  2: { cols: 36, trace: 1 }
};
const DIRS = [[1, 1], [1, -1], [-1, 1], [-1, -1]].map(([x, y]) => [x / Math.SQRT2, y / Math.SQRT2]);
const HUE_SHIFTS = [0, 0.5, 0.33, 0.67];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smooth(a, b, x) { const t = clamp01((x - a) / (b - a)); return t * t * (3 - 2 * t); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// --- les lettres du titre : raster 64 px, champ de distance signé ---------------
export function titleLetters(title) {
  const s = (title || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return s.length >= 2 ? s.slice(0, 4) : '';
}
function textSdf(letters) {
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = TEXT_W; c.height = TEXT_H;
  const g = c.getContext('2d');
  if (!g) return null;
  g.fillStyle = '#000'; g.fillRect(0, 0, TEXT_W, TEXT_H);
  g.fillStyle = '#fff';
  g.font = '900 64px Inter, "Helvetica Neue", Helvetica, Arial, sans-serif';
  g.textAlign = 'center'; g.textBaseline = 'middle';
  g.fillText(letters, TEXT_W / 2, TEXT_H / 2 + 2);
  const px = g.getImageData(0, 0, TEXT_W, TEXT_H).data;
  const inside = new Uint8Array(TEXT_W * TEXT_H);
  for (let i = 0; i < inside.length; i++) inside[i] = px[i * 4] > 127 ? 1 : 0;
  // distance signée, recherche bornée à SDF_RANGE px (R12 : le champ atteint
  // sa valeur de bord bien avant les bords de la texture)
  const out = new Uint8Array(SDF_W * SDF_H * 4);
  const sx = TEXT_W / SDF_W, sy = TEXT_H / SDF_H, R2 = SDF_RANGE * SDF_RANGE;
  for (let j = 0; j < SDF_H; j++) for (let i = 0; i < SDF_W; i++) {
    const x = Math.floor((i + 0.5) * sx), y = Math.floor((j + 0.5) * sy);
    const me = inside[y * TEXT_W + x];
    let best = R2;
    for (let dy = -SDF_RANGE; dy <= SDF_RANGE; dy++) {
      const yy = y + dy; if (yy < 0 || yy >= TEXT_H) continue;
      for (let dx = -SDF_RANGE; dx <= SDF_RANGE; dx++) {
        const xx = x + dx; if (xx < 0 || xx >= TEXT_W) continue;
        if (inside[yy * TEXT_W + xx] !== me) { const d = dx * dx + dy * dy; if (d < best) best = d; }
      }
    }
    const d = Math.sqrt(best) * (me ? -1 : 1);
    const o = (j * SDF_W + i) * 4;
    out[o] = Math.round(clamp01(d / SDF_RANGE * 0.5 + 0.5) * 255); out[o + 3] = 255;
  }
  const t = new THREE.DataTexture(out, SDF_W, SDF_H, THREE.RGBAFormat);
  t.minFilter = t.magFilter = THREE.LinearFilter;
  t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
  t.generateMipmaps = false;
  t.needsUpdate = true;
  return t;
}

const QUAD_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// la mise à jour des hauteurs : un texel par colonne
const UPDATE_FRAGMENT = `
  precision highp float;
  uniform sampler2D uPrev;
  uniform sampler2D uText;
  uniform vec2 uCentre;          // la cellule sous le centre de composition
  uniform float uBeatPhase;
  uniform float uBarCont;        // mesures continues (gelées en montée)
  uniform float uNoiseT;         // croches continues (gelées en montée)
  uniform vec2 uDir;             // direction de l'onde plane
  uniform vec3 uBands;
  uniform float uRise;
  uniform float uFall;
  uniform float uDecay;          // de la trace, par frame
  uniform float uTraceOn;
  uniform float uShape;
  uniform float uLift;
  varying vec2 vUv;
  float h21(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float sdShape(vec2 p) {
    float r = length(p);
    if (uShape < 1.5) return r - 7.0;
    if (uShape < 2.5) return abs(r - 7.5) - 1.6;
    if (uShape < 3.5) return max(min(abs(p.x), abs(p.y)) - 1.6, r - 10.0);
    if (uShape < 4.5) {
      float a = atan(p.y, p.x) / 6.2832;             // -0.5 .. 0.5
      float m = mod(r - a * 3.0, 3.0);
      return max(min(m, 3.0 - m) - 0.75, r - 10.5);
    }
    // le texte : la texture couvre TEXT_CELLS cellules de large
    vec2 uv = p / vec2(${TEXT_CELLS.toFixed(1)}, ${(TEXT_CELLS * SDF_H / SDF_W).toFixed(3)}) + 0.5;
    if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) return 9.0;
    float d = (texture2D(uText, vec2(uv.x, 1.0 - uv.y)).r * 2.0 - 1.0) * ${SDF_RANGE.toFixed(1)};
    return d * ${(TEXT_CELLS / TEXT_W).toFixed(4)};           // px du raster -> cellules
  }
  void main() {
    vec2 cell = floor(vUv * ${N.toFixed(1)});
    vec2 p = cell + 0.5 - uCentre;
    float h = 0.0;
    // l'onde radiale : un anneau par temps, celui du temps précédent s'éteint
    float r = length(p);
    for (int k = 0; k < 2; k++) {
      float age = uBeatPhase + float(k);
      float front = age * ${RADIAL_CELLS_PER_BEAT.toFixed(1)};
      float amp = (0.35 + 1.1 * uBands.x) * exp(-age * 1.1);
      float d = (r - front) / 2.2;
      h += amp * exp(-d * d);
    }
    // l'onde plane diagonale : une période par mesure
    h += (0.3 + 0.8 * uBands.y) * 0.5 * (1.0 + sin(dot(p, uDir) * 0.45 - uBarCont * 6.2832));
    // le bruit fin, d'une colonne à l'autre, renouvelé à chaque croche
    float i = floor(uNoiseT), f = fract(uNoiseT); f = f * f * (3.0 - 2.0 * f);
    float n = mix(h21(cell + i * 0.37), h21(cell + (i + 1.0) * 0.37), f);
    h += (0.10 + 0.5 * uBands.z) * n;
    // la forme, vue d'en haut
    if (uShape > 0.5 && uLift > 0.001) h += uLift * 1.7 * smoothstep(0.7, -0.7, sdShape(p));
    h += uRise;
    h = ${FLOOR.toFixed(2)} + h;
    h = mix(h, ${FLOOR.toFixed(2)} * 0.85, uFall);          // le drop : tout retombe d'un coup
    h = min(h, ${(HSCALE - 0.1).toFixed(2)});
    vec4 prev = texture2D(uPrev, vUv);
    float trace = max(prev.g * uDecay, step(${TRACE_H.toFixed(2)}, h) * uTraceOn);
    float accent = smoothstep(${(TRACE_H - 0.45).toFixed(2)}, ${(TRACE_H + 0.2).toFixed(2)}, h);
    gl_FragColor = vec4(h / ${HSCALE.toFixed(2)}, trace, accent, 1.0);
  }
`;

// le champ : lancer de rayon orthographique, DDA sur les cellules
const FIELD_FRAGMENT = `
  precision highp float;
  uniform sampler2D uH;
  uniform vec2 uRes;
  uniform float uPxWorld;        // unités monde par pixel (le rayon)
  uniform float uPxRim;          // unités monde par pixel ÉCRAN (les liserés : la mesure 32 x 32 garde les largeurs de l'écran)
  uniform vec2 uCentre;
  uniform vec3 uLight;
  uniform vec3 uMid;
  uniform vec3 uDark;
  uniform vec3 uAccent;
  uniform float uInv;
  uniform float uRim;            // 1 au repos, flashe sur le temps
  uniform float uGain;
  uniform float uDebug;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  const float SQRT3 = 1.7320508, INV_SQRT3 = 0.57735027, INV_SQRT2 = 0.70710678;
  const vec3 D = vec3(-INV_SQRT3);                       // la direction du regard
  const vec3 RGT = vec3(INV_SQRT2, -INV_SQRT2, 0.0);     // la droite de l'écran
  const vec3 UP = vec3(-0.40824829, -0.40824829, 0.81649658);
  vec4 heightAt(vec2 cell) {
    vec4 t = texture2D(uH, (cell + 0.5) / ${N.toFixed(1)});
    float h = t.r * ${HSCALE.toFixed(2)};
    h = mix(h, ${HINV.toFixed(2)} - h, uInv);              // l'inversion : des profondeurs
    return vec4(h, t.g, t.b, 0.0);
  }
  void main() {
    vec2 uv = gl_FragCoord.xy / uRes;
    if (uDebug > 0.5) {
      vec4 t = texture2D(uH, uv);
      gl_FragColor = vec4(vec3(t.r) + vec3(0.0, t.g * 0.5, 0.0) + vec3(t.b * 0.3, 0.0, 0.0), 1.0);
      return;
    }
    // L'ESCALIER : la hauteur vraie de la colonne (cx, cy) est
    // h(cx, cy) + N - cx - cy, une marche de 1 plus haute que ses voisines
    // côté caméra : chaque colonne montre son dessus et ses deux faces, le
    // motif des blocs culbutants. On marche en zz = z + cx + cy - N, la
    // hauteur relative à la marche : dans une cellule seul z bouge, et zz
    // perd 1 à chaque changement de cellule.
    vec2 s = (uv - 0.5) * uRes * uPxWorld;
    vec3 P = s.x * RGT + s.y * UP;
    vec2 q0 = P.xy + uCentre;
    float u0 = P.z + q0.x + q0.y - ${N.toFixed(1)};
    // au-dessus de tout : u = ZTOP + 2 garantit zz >= ZTOP > toute hauteur
    float t0 = (u0 - (${HTOP.toFixed(2)} + 2.0)) * INV_SQRT3;
    P += D * t0;
    vec2 pos = P.xy + uCentre;
    vec2 cell = floor(pos);
    float base = cell.x + cell.y - ${N.toFixed(1)};
    float Z0 = P.z;
    float tx = (pos.x - cell.x) * SQRT3, ty = (pos.y - cell.y) * SQRT3;
    float t = 0.0, tHit = -1.0;
    int face = 0;                                          // 0 dessus, 1 face +x, 2 face +y
    vec4 H = heightAt(cell);
    for (int i = 0; i < ${MAX_STEPS}; i++) {
      float tn = min(tx, ty);
      if (Z0 + base - tn * INV_SQRT3 <= H.x) { tHit = (Z0 + base - H.x) * SQRT3; face = 0; break; }
      if (tx < ty) { t = tx; tx += SQRT3; cell.x -= 1.0; face = 1; }
      else { t = ty; ty += SQRT3; cell.y -= 1.0; face = 2; }
      base -= 1.0;
      H = heightAt(cell);
      if (Z0 + base - t * INV_SQRT3 <= H.x) { tHit = t; break; }
    }
    vec3 col = vec3(0.0);
    if (tHit >= 0.0) {
      vec3 Q = P + D * tHit;
      float zz = Z0 + base - tHit * INV_SQRT3;              // z relatif à la marche de la cellule touchée
      vec2 f = fract(Q.xy + uCentre);
      if (face == 1) f.x = 1.0;
      if (face == 2) f.y = 1.0;
      // les trois tons, clair et sombre échangés par l'inversion
      vec3 top = mix(uLight, uDark, uInv), side2 = mix(uDark, uLight, uInv);
      vec3 tone = face == 0 ? top : (face == 1 ? uMid : side2);
      // la distance aux arêtes, en pixels : la même conversion d/√2/px pour
      // les arêtes des dessus et celles des faces (voir l'en-tête)
      float dEdge;
      if (face == 0) dEdge = min(min(f.x, 1.0 - f.x), min(f.y, 1.0 - f.y));
      else {
        float along = face == 1 ? f.y : f.x;
        dEdge = min(H.x - zz, min(along, 1.0 - along));
      }
      float dpx = dEdge * INV_SQRT2 / uPxRim;
      col = tone;
      if (face == 0) {
        col = mix(col, uAccent * 0.6, H.z * 0.65);           // les sommets hauts : l'accent
        col += uAccent * H.y * 0.4;                           // la trace, qui s'éteint en un temps
      }
      // le liseré (R1) REMPLACE le ton (un cœur AJOUTÉ à une face claire
      // passait le seuil de bloom sur 1 500 arêtes : un voile blanc). Cœur de
      // 1,2 px à 0,60 au repos, 0,76 sur le flash du temps : TOUJOURS sous le
      // seuil (0,78) - à cette densité, un seul flash au-dessus voile tout
      // l'écran ; seuls les sommets hauts (clairsemés) le passent.
      // Gaine accent 1,2-3 px.
      float core = 1.0 - smoothstep(0.6, 1.4, dpx);
      float sheath = 1.0 - smoothstep(1.2, 3.0, dpx);
      float coreAmp = min(0.60 * uRim, 0.76) + (face == 0 ? 0.35 * H.z : 0.0);
      col = mix(col, uAccent * (0.45 + 0.25 * (uRim - 1.0)), sheath * 0.8);
      col = mix(col, vec3(1.0, 0.98, 0.95) * coreAmp, core);
    }
    col *= uGain;
    vec2 sc = vec2((uv.x * 2.0 - 1.0) * uAspect, uv.y * 2.0 - 1.0);
    vec2 qs = abs(sc - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(qs, 0.0)) + min(max(qs.x, qs.y), 0.0);
    col *= mix(0.4, 1.0, smoothstep(0.0, uSafeFeather, sd));
    gl_FragColor = vec4(col, 1.0);
  }
`;

export class IsocubesVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    this._forceInv = urlParam('isoinvert') === '1';
    const sh = (urlParam('isoshape') || '').toLowerCase();
    this._forceShape = SHAPE_ID[sh] ? sh : null;
    this._debugHeight = urlParam('isoheight') === '1';
    this._noComfort = urlParam('isogain') === '0';   // debug : sans le gouverneur de luminance
    this._post = new VdjPost({ bloomStrength: 0.3, bloomThreshold: 0.78, grain: 0.012, vignette: 0.35, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort(VDJ_PRESETS.party.comfort);
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    this._adapt = new Adapt();
    this._clock = new SceneClock({ period: SCENE_S, start: 0 });
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 1);
    this._textTex = textSdf('HALO') || new THREE.DataTexture(new Uint8Array([128, 0, 0, 255]), 1, 1, THREE.RGBAFormat);
    this._textTex.needsUpdate = true;
    this._letters = '';

    this._uu = {
      uPrev: { value: null }, uText: { value: this._textTex }, uCentre: { value: new THREE.Vector2(N / 2, N / 2) },
      uBeatPhase: { value: 0 }, uBarCont: { value: 0 }, uNoiseT: { value: 0 }, uDir: { value: new THREE.Vector2(DIRS[0][0], DIRS[0][1]) },
      uBands: { value: new THREE.Vector3(0.3, 0.3, 0.3) }, uRise: { value: 0 }, uFall: { value: 0 }, uDecay: { value: 0.9 },
      uTraceOn: { value: this._layout.trace }, uShape: { value: 0 }, uLift: { value: 0 }
    };
    this._updScene = new THREE.Scene();
    const uq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._uu, vertexShader: QUAD_VERTEX, fragmentShader: UPDATE_FRAGMENT, depthTest: false, depthWrite: false }));
    uq.frustumCulled = false;
    this._updScene.add(uq);
    this._updMesh = uq;

    this._light = new THREE.Color(0.7, 0.6, 0.9); this._mid = new THREE.Color(0.3, 0.2, 0.5); this._dark = new THREE.Color(0.05, 0.03, 0.1); this._accent = new THREE.Color(0.1, 0.9, 0.9);
    this._u = {
      uH: { value: null }, uRes: { value: new THREE.Vector2(1920, 1080) }, uPxWorld: { value: 0.03 }, uPxRim: { value: 0.03 }, uCentre: { value: this._uu.uCentre.value },
      uLight: { value: this._light }, uMid: { value: this._mid }, uDark: { value: this._dark }, uAccent: { value: this._accent },
      uInv: { value: 0 }, uRim: { value: 1 }, uGain: { value: 1 }, uDebug: { value: this._debugHeight ? 1 : 0 },
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._u, vertexShader: QUAD_VERTEX, fragmentShader: FIELD_FRAGMENT, depthTest: false, depthWrite: false }));
    this._quad.frustumCulled = false; this._quad.renderOrder = -3000;
    const self = this;
    this._quad.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._quad);
    // la mesure du confort : le même shader sur 32 x 32
    this._measScene = new THREE.Scene();
    const mq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._quad.material);
    mq.frustumCulled = false;
    this._measScene.add(mq);
    this._measRT = new THREE.WebGLRenderTarget(32, 32, { type: THREE.UnsignedByteType, depthBuffer: false, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
    this._measBuf = new Uint8Array(32 * 32 * 4);

    this._time = 0; this._beats = 0; this._beatCont = 0; this._bars = 0; this._barCont = 0; this._noiseT = 0;
    this._beatAge = 9; this._barAge = 9; this._dir = 0; this._palette = 0; this._paletteRef = undefined;
    this._build = 0; this._fall = 0; this._dropAge = 99; this._inv = 0; this._invTarget = 0; this._invPending = false; this._invCount = 0;
    this._shape = 0; this._shapeT0 = -99; this._shapeIdx = 0; this._shapeDone = false; this._phraseN = 0;
    this._lumAge = 0; this._lumMeasured = 0.3; this._lumFresh = false; this._gain = 1; this._pending = true; this._paused = false;
    this._applyPalette(null);
  }

  _applyPalette(pal) {
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors[0] : 0x7c5cff;
    const base = vdjColor(v, new THREE.Color(), VDJ_PRESETS.party.saturation);
    base.offsetHSL(HUE_SHIFTS[this._palette % HUE_SHIFTS.length], 0, 0);
    // trois tons francs, tous sous le seuil de bloom (seuls les cœurs des
    // liserés le passent) : clair ~0,45 linéaire, moyen 0,16, sombre 0,035
    this._light.copy(base).lerp(new THREE.Color(1, 1, 1), 0.25).multiplyScalar(0.42);
    this._mid.copy(base).multiplyScalar(0.18);
    this._dark.copy(base).multiplyScalar(0.04);
    const acc = base.clone(); acc.offsetHSL(0.5, 0, 0);
    this._accent.copy(acc);
  }

  onTrackChange(profile) {
    const letters = titleLetters(profile && profile.title);
    if (letters !== this._letters) {
      this._letters = letters;
      const t = textSdf(letters || 'HALO');
      if (t) { if (this._textTex) this._textTex.dispose(); this._textTex = t; this._uu.uText.value = t; }
    }
  }

  _startShape(now) {
    let name;
    if (this._forceShape) name = this._forceShape;
    else {
      const list = SHAPES.filter((s) => s !== 'text' || this._letters);
      name = list[this._shapeIdx % list.length]; this._shapeIdx++;
    }
    this._shape = SHAPE_ID[name]; this._shapeT0 = now;
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    this._paused = paused;
    const dt = paused ? 0 : Math.min(deltaTime, 0.05);
    const a = this._adapt.update(dt, profile);
    if (profile.artworkPalette !== this._paletteRef) { this._paletteRef = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }
    if (this._letters === '' && profile.title) this.onTrackChange(profile);
    if (paused) return;                                  // l'image tient : aucune mise à jour
    this._time += dt;
    this._clock.update(dt);
    if (this._clock.wrapped) { this._invCount = 0; this._shapeDone = false; }
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 124;
    const beatS = 60 / bpm, barS = 4 * beatS;
    const director = profile.director || null;

    // la montée : un drop connu qui approche, ou le segment « build »
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._build += (build - this._build) * (1 - Math.exp(-dt / 1.0));
    const freeze = 1 - 0.85 * smooth(0.35, 0.8, this._build);

    // les horloges musicales
    if (a.beat) { this._beats++; this._beatAge = 0; }
    this._beatAge += dt;
    const bp = a.beatPhase != null ? a.beatPhase : clamp01(this._beatAge / beatS);
    this._beatCont = this._beats + bp;
    if (a.bar) {
      this._bars++; this._barAge = 0;
      this._dir = (this._dir + 1 + Math.floor(Math.random() * 3)) % 4;    // une autre diagonale
      if (this._invPending && this._invCount < 2) { this._invTarget = 1 - this._invTarget; this._invCount++; this._invPending = false; }
    }
    this._barAge += dt;
    this._barCont += dt / barS * freeze;
    this._noiseT += dt / beatS * 2 * freeze;
    if (a.phrase) {
      this._phraseN++;
      this._palette++; this._applyPalette(profile.artworkPalette);
      if (this._clock.t > SHAPE_AT_S + 2 && this._shape === 0 && this._phraseN % 2 === 0) this._startShape(this._time);
    }
    // la forme de la scène, vers 6 s
    if (!this._shapeDone && this._clock.t >= SHAPE_AT_S && this._shape === 0) { this._shapeDone = true; this._startShape(this._time); }
    // le drop : tout retombe, flash, inversion sur la mesure suivante
    if (a.drop) {
      this._dropAge = 0;
      this._invPending = true;
      if (director && this._comfort.requestFlash(this._time)) director.flash = VDJ_DROP_FLASH;
    }
    this._dropAge += dt;
    this._fall = this._dropAge < 2 ? Math.exp(-this._dropAge / (beatS * 0.45)) : 0;
    // l'inversion : fondu d'un demi-temps
    const invT = this._forceInv ? 1 : this._invTarget;
    this._inv += (invT - this._inv) * (1 - Math.exp(-dt / (beatS * 0.5 / 3)));

    // la forme : lève en 1 mesure, tient 2, retombe en 1
    let lift = 0;
    if (this._shape) {
      const st = this._time - this._shapeT0;
      lift = this._forceShape ? 1 : smooth(0, barS, st) * (1 - smooth(3 * barS, 4 * barS, st));
      if (!this._forceShape && st > 4 * barS) this._shape = 0;
    } else if (this._forceShape) { this._startShape(this._time); lift = 1; }

    const uu = this._uu;
    uu.uBeatPhase.value = bp;
    uu.uBarCont.value = this._barCont % 64;
    uu.uNoiseT.value = this._noiseT % 4096;
    uu.uDir.value.set(DIRS[this._dir][0], DIRS[this._dir][1]);
    const b = a.bands || { low: 0.4 + 0.4 * a.pulse, mid: 0.4 * a.intensity, high: 0.3 * a.intensity };
    uu.uBands.value.set(clamp01(b.low), clamp01(b.mid), clamp01(b.high));
    uu.uRise.value = 1.5 * this._build * this._build;
    uu.uFall.value = this._fall;
    uu.uDecay.value = Math.pow(0.5, dt / (beatS / 3));
    uu.uShape.value = this._shape; uu.uLift.value = lift;
    this._pending = true;

    const u = this._u;
    u.uInv.value = this._inv;
    // le temps : les liserés flashent (60 ms de montée, retour en 250 ms)
    const ba = this._beatAge;
    const env = ba < 0.06 ? ba / 0.06 : Math.exp(-(ba - 0.06) / 0.15);
    u.uRim.value = 1 + 0.8 * env * (0.5 + 0.5 * uu.uBands.value.x);
    this._zoom = vdjCameraKick(a.barPhase != null ? a.barPhase : clamp01(this._barAge / barS), this._bars, 4).zoom;
    // le confort : VdjComfort n'est nourri qu'à chaque mesure FRAÎCHE (0,5 s),
    // sinon son pas multiplicatif (x0,7 par appel) s'emballe sur une mesure
    // vieille d'une demi-seconde et le gain tombe à zéro
    this._lumAge += dt;
    if (this._lumFresh) { this._lumFresh = false; this._gain = this._comfort.measure(this._lumMeasured, 0.5); }
    u.uGain.value = this._noComfort ? 1 : this._gain;
    this._post.update(vdjBeatChroma(bp, beatS));
    this._bp = bp; this._lift = lift;
  }

  _ensureTargets(renderer) {
    if (this._rt) return;
    const ext = renderer.extensions;
    const floatOk = renderer.capabilities.isWebGL2 && (ext.has('EXT_color_buffer_float') || ext.has('EXT_color_buffer_half_float'));
    const mk = () => new THREE.WebGLRenderTarget(N, N, {
      type: floatOk ? THREE.HalfFloatType : THREE.UnsignedByteType,
      minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, depthBuffer: false,
      wrapS: THREE.RepeatWrapping, wrapT: THREE.RepeatWrapping, generateMipmaps: false
    });
    this._rt = { a: mk(), b: mk() };
    const prev = renderer.getRenderTarget();
    for (const t of [this._rt.a, this._rt.b]) { renderer.setRenderTarget(t); renderer.clear(true, false, false); }
    renderer.setRenderTarget(prev);
    this._floatOk = floatOk;
  }

  _render(renderer) {
    this._ensureTargets(renderer);
    // la taille de la CIBLE courante (la passe de rendu du composer), pas du
    // canvas : gl_FragCoord vit dans la cible, et au tier 0 elle est plus
    // petite - avec la taille du canvas la planche ne remplissait que le
    // coin bas-gauche
    const cur = renderer.getRenderTarget();
    if (cur) _size.set(cur.width, cur.height); else renderer.getDrawingBufferSize(_size);
    const w = Math.max(2, _size.x), h = Math.max(2, _size.y);
    const prev = renderer.getRenderTarget();
    renderer.getClearColor(_cc); const ca = renderer.getClearAlpha();
    const autoClear = renderer.autoClear;
    renderer.autoClear = false;
    if (this._pending) {
      this._pending = false;
      this._uu.uPrev.value = this._rt.a.texture;
      renderer.setRenderTarget(this._rt.b);
      renderer.render(this._updScene, this._ortho);
      const t = this._rt.a; this._rt.a = this._rt.b; this._rt.b = t;
    }
    this._u.uH.value = this._rt.a.texture;
    // l'échelle : cols colonnes (hexagones de largeur √2 monde) sur la largeur
    const pxWorld = this._layout.cols * Math.SQRT2 / w / (this._zoom || 1);
    // la mesure du confort, toutes les 0,5 s, sur 32 x 32 avec le même
    // shader : le rayon à l'échelle du petit rendu, les liserés à celle de
    // l'écran (sinon tout pixel est « au bord » et la mesure lit blanc)
    if (this._lumAge >= 0.5 && !this._paused) {
      this._lumAge = 0;
      this._u.uRes.value.set(32, 32);
      this._u.uPxWorld.value = pxWorld * w / 32;
      this._u.uPxRim.value = pxWorld;
      renderer.setRenderTarget(this._measRT);
      renderer.setClearColor(0x000000, 1);
      renderer.clear(true, false, false);
      renderer.render(this._measScene, this._ortho);
      try {
        renderer.readRenderTargetPixels(this._measRT, 0, 0, 32, 32, this._measBuf);
        let sum = 0;
        for (let i = 0; i < this._measBuf.length; i += 4) sum += 0.2126 * this._measBuf[i] + 0.7152 * this._measBuf[i + 1] + 0.0722 * this._measBuf[i + 2];
        this._lumMeasured = sum / (32 * 32 * 255);
        this._lumFresh = true;
      } catch (e) { /* la mesure attendra la prochaine fois */ }
      renderer.setClearColor(_cc, ca);
    }
    this._u.uRes.value.set(w, h);
    this._u.uPxWorld.value = pxWorld;
    this._u.uPxRim.value = pxWorld;
    renderer.setRenderTarget(prev);
    renderer.autoClear = autoClear;
  }

  getProbe() {
    return {
      t: +this._time.toFixed(2), scene: +(this._clock ? this._clock.t : 0).toFixed(2), beats: this._beats, bars: this._bars,
      inv: +this._inv.toFixed(3), invCount: this._invCount, shape: this._shape, lift: +(this._lift || 0).toFixed(3),
      build: +this._build.toFixed(3), fall: +this._fall.toFixed(3), lum: +this._lumMeasured.toFixed(3), gain: +this._u.uGain.value.toFixed(3),
      cols: this._layout.cols, letters: this._letters, floatOk: !!this._floatOk
    };
  }

  dispose() {
    if (this._quad) { this._quad.onBeforeRender = null; if (this._scene) this._scene.remove(this._quad); this._quad.geometry.dispose(); this._quad.material.dispose(); }
    if (this._updMesh) { this._updMesh.geometry.dispose(); this._updMesh.material.dispose(); }
    if (this._rt) { this._rt.a.dispose(); this._rt.b.dispose(); }
    if (this._measRT) this._measRT.dispose();
    if (this._textTex) this._textTex.dispose();
    if (this._adapt) this._adapt.dispose();
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _cc = new THREE.Color();
